import React, { useEffect, useState } from "react";
import { FormControl, InputGroup } from "react-bootstrap";
import { FaRegEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";
import { getAuthLinkParams } from "../../modules/helpers";
import { handleErrors } from "../../modules/handleErrors";
import AuthPageLayout, {
  AuthLoadingState,
  AuthPrimaryButton,
  AuthSecondaryLink,
  AuthStatusIcon,
} from "./AuthPageLayout";

const STATES = {
  LOADING: "loading",
  INVALID: "invalid",
  ERROR: "error",
  ALREADY_ACTIVE: "already_active",
  SUCCESS: "success",
  RESEND_SUCCESS: "resend_success",
};

const Activate = () => {
  const navigate = useNavigate();
  const { uid, token } = getAuthLinkParams();

  const [state, setState] = useState(STATES.LOADING);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    "Your account has been activated successfully."
  );
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoader, setResendLoader] = useState(false);

  useEffect(() => {
    const runActivationFlow = async () => {
      if (!uid || !token) {
        setState(STATES.INVALID);
        setErrorMessage("This activation link is invalid or incomplete.");
        return;
      }

      try {
        const verifyResponse = await apiClient.post("/verify-activation/", {
          uid,
          token,
        });

        if (!verifyResponse.ok) {
          const data = verifyResponse.data;
          if (data?.valid === false) {
            setState(STATES.ERROR);
            setErrorMessage(
              data.error || "Activation link is invalid or has expired."
            );
            return;
          }
          setState(STATES.ERROR);
          setErrorMessage(
            data?.error ||
              data?.message ||
              "Unable to verify your activation link. Please try again."
          );
          return;
        }

        const { valid, already_active, email: userEmail } = verifyResponse.data;

        if (!valid) {
          setState(STATES.ERROR);
          setErrorMessage("Activation link is invalid or has expired.");
          return;
        }

        if (userEmail) setEmail(userEmail);

        if (already_active) {
          setState(STATES.ALREADY_ACTIVE);
          return;
        }

        const activateResponse = await apiClient.post("/activate-account/", {
          uid,
          token,
        });

        if (!activateResponse.ok) {
          setState(STATES.ERROR);
          setErrorMessage(
            activateResponse.data?.error ||
              "Activation link is invalid or has expired."
          );
          return;
        }

        if (activateResponse.data?.message) {
          setSuccessMessage(activateResponse.data.message);
        }

        setState(STATES.SUCCESS);
      } catch {
        setState(STATES.ERROR);
        setErrorMessage("Something went wrong. Please try again later.");
      }
    };

    runActivationFlow();
  }, [uid, token]);

  const handleOpenResend = () => {
    setResendEmail(email);
    setShowResendForm(true);
  };

  const handleResendSubmit = async (e) => {
    e.preventDefault();

    try {
      setResendLoader(true);
      const response = await apiClient.post("/resend-activation/", {
        email: resendEmail,
      });
      setResendLoader(false);

      if (!response.ok) return handleErrors(response.data);

      setEmail(resendEmail);
      setState(STATES.RESEND_SUCCESS);
    } catch {
      setResendLoader(false);
      toast.error("Something went wrong. Please try again later.");
    }
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, start, middle, domain) => {
        const hidden = middle.length > 0 ? "••••" : "";
        return `${start}${hidden}${domain}`;
      })
    : null;

  const renderResendForm = () => (
    <>
      <AuthStatusIcon type="info" />
      <h5 className="mb-2">Resend activation email</h5>
      <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
        Enter your email address and we&apos;ll send you a new activation link.
      </p>
      <form onSubmit={handleResendSubmit} className="text-start">
        <div className="form-group mb-3">
          <label
            htmlFor="resend-email"
            className="form-label mb-1"
            style={{ fontSize: "13px", color: "#4a4a4a" }}
          >
            Email address
          </label>
          <InputGroup>
            <InputGroup.Text
              className="position-absolute bg-white border-0 px-2"
              style={{
                zIndex: 10,
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <FaRegEnvelope style={{ color: "#868B96" }} />
            </InputGroup.Text>
            <FormControl
              required
              id="resend-email"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="form-control rounded-5 border-0 ps-5 pe-3 py-2 shadow-sm"
              style={{ height: "50px", fontSize: "13px" }}
            />
          </InputGroup>
        </div>

        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center mt-4">
          <AuthPrimaryButton type="submit" disabled={resendLoader}>
            {resendLoader ? "Sending..." : "Send activation email"}
          </AuthPrimaryButton>
          <button
            type="button"
            className="btn btn-link border-0 p-0"
            style={{ color: "#000", fontSize: "14px", fontWeight: 500 }}
            onClick={() => {
              setShowResendForm(false);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );

  const renderContent = () => {
    switch (state) {
      case STATES.LOADING:
        return <AuthLoadingState message="Verifying your activation link..." />;

      case STATES.INVALID:
      case STATES.ERROR:
        if (showResendForm) return renderResendForm();
        return (
          <>
            <AuthStatusIcon type="error" />
            <h5 className="mb-2">Activation failed</h5>
            <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
              {errorMessage}
            </p>
            <div className="d-flex flex-column gap-3 justify-content-center align-items-center">
              <AuthPrimaryButton onClick={handleOpenResend}>
                Resend activation email
              </AuthPrimaryButton>
              <AuthSecondaryLink to="/login">Go to login</AuthSecondaryLink>
            </div>
            <p className="mt-3 mb-0">
              <AuthSecondaryLink to="/about-us">Contact support</AuthSecondaryLink>
            </p>
          </>
        );

      case STATES.RESEND_SUCCESS:
        return (
          <>
            <AuthStatusIcon type="success" />
            <h5 className="mb-2">Check your email</h5>
            <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
              We&apos;ve sent a new activation link to{" "}
              <strong>{resendEmail || email}</strong>. Open the link in your
              email to activate your account.
            </p>
            <p className="mb-4" style={{ fontSize: "13px", color: "#868B96" }}>
              Didn&apos;t receive it? Check your spam folder or try again in a
              few minutes.
            </p>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
              <AuthPrimaryButton
                onClick={() => {
                  setState(STATES.ERROR);
                  setShowResendForm(true);
                  setResendEmail(resendEmail || email);
                }}
              >
                Send again
              </AuthPrimaryButton>
              <AuthSecondaryLink to="/login">Back to login</AuthSecondaryLink>
            </div>
          </>
        );

      case STATES.ALREADY_ACTIVE:
        return (
          <>
            <AuthStatusIcon type="info" />
            <h5 className="mb-2">Account already activated</h5>
            <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
              {maskedEmail ? (
                <>
                  The account for <strong>{maskedEmail}</strong> is already
                  active. You can sign in with your credentials.
                </>
              ) : (
                "Your account is already active. You can sign in with your credentials."
              )}
            </p>
            <AuthPrimaryButton onClick={() => navigate("/login")}>
              Go to login
            </AuthPrimaryButton>
          </>
        );

      case STATES.SUCCESS:
        return (
          <>
            <AuthStatusIcon type="success" />
            <h5 className="mb-2">You&apos;re all set!</h5>
            <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
              {successMessage}
              {maskedEmail && (
                <>
                  {" "}
                  Welcome to Mockee — <strong>{maskedEmail}</strong> is ready to
                  use.
                </>
              )}
            </p>
            <AuthPrimaryButton onClick={() => navigate("/login")}>
              Go to login
            </AuthPrimaryButton>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <AuthPageLayout
      title={state === STATES.LOADING ? "Activating your account" : undefined}
      subtitle={
        state === STATES.LOADING
          ? "We're confirming your email link. This only takes a moment."
          : undefined
      }
    >
      {renderContent()}
    </AuthPageLayout>
  );
};

export default Activate;
